/**
 * CoConnect — WeatherWidget
 *
 * Displays current weather + 7-day forecast for a farm location.
 * Compact card suitable for embedding in the farmer dashboard overview.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import { Spinner, Alert } from './ui';

interface WeatherData {
  current: {
    temperature_c: number;
    feels_like_c: number;
    humidity_pct: number;
    pressure_hpa: number;
    wind_speed_kmh: number;
    wind_direction_deg: number;
    precipitation_mm: number;
    cloud_cover_pct: number;
    weather_code: number;
    weather_description: string;
    is_day: boolean;
    observed_at: string;
  };
  hourly: any[];
  daily: Array<{
    date: string;
    temp_min_c: number;
    temp_max_c: number;
    precipitation_mm: number;
    precipitation_probability_max_pct: number;
    wind_speed_max_kmh: number;
    humidity_avg_pct: number;
    weather_code: number;
  }>;
  provider: string;
  fetched_at: string;
  cached: boolean;
}

// WMO code → emoji (Open-Meteo standard)
function wmoEmoji(code: number, isDay: boolean = true): string {
  if (code === 0) return isDay ? '☀️' : '🌙';
  if (code === 1 || code === 2) return isDay ? '🌤️' : '☁️';
  if (code === 3) return '☁️';
  if (code >= 45 && code <= 48) return '🌫️';
  if (code >= 51 && code <= 57) return '🌦️';
  if (code >= 61 && code <= 65) return '🌧️';
  if (code >= 66 && code <= 67) return '🌧️❄️';
  if (code >= 71 && code <= 77) return '🌨️';
  if (code >= 80 && code <= 82) return '🌧️';
  if (code >= 85 && code <= 86) return '🌨️';
  if (code >= 95 && code <= 99) return '⛈️';
  return '🌡️';
}

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Props {
  lat?: number | null;
  lng?: number | null;
  farmId?: string;
  farmName?: string;
  compact?: boolean;
}

const WeatherWidget: React.FC<Props> = ({ lat, lng, farmId, farmName, compact = false }) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWeather = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = '/weather/';
      if (farmId) {
        url = `/weather/farm/${farmId}`;
      } else if (lat != null && lng != null) {
        url = `/weather/current?lat=${lat}&lng=${lng}`;
      } else {
        setError('No location available. Map a farm boundary to enable weather.');
        setLoading(false);
        return;
      }
      const res = await apiClient.get(url);
      // Handle both response shapes:
      //   /weather/current → returns weather object directly
      //   /weather/farm/:id → returns { farm: {...}, weather: {...} }
      const weatherData = res.data?.weather || res.data;
      // Defensive: ensure hourly and daily arrays exist
      if (weatherData && !weatherData.daily) weatherData.daily = [];
      if (weatherData && !weatherData.hourly) weatherData.hourly = [];
      setWeather(weatherData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load weather');
    } finally {
      setLoading(false);
    }
  }, [farmId, lat, lng]);

  useEffect(() => { fetchWeather(); }, [fetchWeather]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner size="sm" /> <span className="ml-2 text-sm text-ink-500 dark:text-ink-400">Loading weather…</span>
      </div>
    );
  }

  if (error) {
    return <Alert tone="warning">{error}</Alert>;
  }

  if (!weather || !weather.current) return null;

  const cur = weather.current;
  const next7d = (weather.daily || []).slice(0, 7);
  const next24h = (weather.hourly || []).slice(0, 24);
  const total24hRain = next24h.reduce((s, h) => s + (h.precipitation_mm || 0), 0);

  return (
    <div className="farmer-weather-widget space-y-3">
      {/* Current weather */}
      <div className="farmer-weather-current flex items-center justify-between">
        <div className="farmer-weather-primary flex items-center gap-3">
          <span className="text-4xl leading-none">{wmoEmoji(cur.weather_code, cur.is_day)}</span>
          <div>
            <p className="text-3xl font-semibold nums text-ink-900 dark:text-ink-50">
              {Number(cur.temperature_c).toFixed(0)}<span className="text-lg text-ink-500 dark:text-ink-400">°C</span>
            </p>
            <p className="text-sm text-ink-600 dark:text-ink-300 capitalize">{cur.weather_description}</p>
            <p className="text-2xs text-ink-500 dark:text-ink-400">Feels like {Number(cur.feels_like_c).toFixed(0)}°C</p>
          </div>
        </div>
        <div className="farmer-weather-details text-right text-2xs text-ink-500 dark:text-ink-400 nums space-y-0.5">
          <p>💧 RH {cur.humidity_pct}%</p>
          <p>🌬️ {Number(cur.wind_speed_kmh).toFixed(0)} km/h</p>
          <p>🌧️ {Number(cur.precipitation_mm).toFixed(1)} mm/h</p>
          {total24hRain > 0 && <p className="text-info-700 dark:text-info-500 font-medium">+{total24hRain.toFixed(1)}mm next 24h</p>}
        </div>
      </div>

      {!compact && next7d.length > 0 && (
        <>
          {/* 7-day forecast strip */}
          <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
            <p className="section-eyebrow mb-2">7-day forecast</p>
            <div className="farmer-weather-forecast grid grid-cols-7 gap-1">
              {next7d.map((d, i) => {
                const date = new Date(d.date);
                const dayName = i === 0 ? 'Today' : dayNames[date.getDay()];
                return (
                  <div key={d.date} className="text-center p-1.5 rounded-md hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors">
                    <p className="text-2xs font-medium text-ink-600 dark:text-ink-300 mb-1">{dayName}</p>
                    <p className="text-lg leading-none mb-0.5">{wmoEmoji(d.weather_code, true)}</p>
                    <p className="text-2xs nums font-semibold text-ink-900 dark:text-ink-50">{Number(d.temp_max_c).toFixed(0)}°</p>
                    <p className="text-2xs nums text-ink-500 dark:text-ink-400">{Number(d.temp_min_c).toFixed(0)}°</p>
                    {d.precipitation_mm > 0 && (
                      <p className="text-3xs nums text-info-700 dark:text-info-500">{Number(d.precipitation_mm).toFixed(0)}mm</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className="farmer-weather-source flex items-center justify-between text-2xs text-ink-400 dark:text-ink-500 border-t border-ink-200 dark:border-ink-800 pt-2">
        <span>Source: {weather.provider}{weather.cached ? ' (cached)' : ''}</span>
        <button onClick={fetchWeather} className="hover:text-ink-600 dark:hover:text-ink-300">↻ Refresh</button>
      </div>
    </div>
  );
};

export default WeatherWidget;
